<template>
    <div class="container">
        <DsModal
            size="xl"
            :is-open="isOpen"
        >
            <template #heading>
                <DsModalHeader>
                    <template #leading>
                        <DsIconButton name="x" :aria-label="$t('global.close')" @click="close" />
                    </template>

                    <template #title>
                        <DsModalHeaderTitle>
                            <template #title>
                                {{ title }}
                            </template>
                        </DsModalHeaderTitle>
                    </template>

                    <template #trailing>
                        <div v-if="isRefreshing" class="refresh-spinner-container">
                            <DsSpinner :size="24" />
                        </div>

                        <DsIconButton
                            v-else
                            :aria-label="$t('global.refresh')"
                            name="refresh-cw"
                            @click="refresh"
                        />
                    </template>
                </DsModalHeader>
            </template>

            <template #default>
                <div class="record-table">
                    <DsTableGrid v-if="tableRecords.length" :data="tableRecords">
                        <DsTableGridColumn
                            :label="$t('settings.email.domainAuthStatus.labels.record')"
                            prop="recordNumber"
                            width="10"
                        />

                        <DsTableGridColumn
                            :label="$t('settings.email.domainAuthStatus.labels.type')"
                            prop="recordType"
                            width="20"
                        />

                        <DsTableGridColumn
                            :label="$t('settings.email.domainAuthStatus.labels.host')"
                        >
                            <template #default="{ host }">
                                <span
                                    class="copy-input"
                                    data-qa="dns-record-host"
                                    disabled
                                    @click="copy(host)"
                                >{{ host }}.{{ domain.domain }}<DsIcon class="copy-icon" name="copy" /></span>
                            </template>
                        </DsTableGridColumn>

                        <DsTableGridColumn
                            :label="$t('settings.email.domainAuthStatus.labels.value')"
                        >
                            <template #default="{ value }">
                                <span class="copy-input" data-qa="dns-record-value" @click="copy(value)">{{ value }}<DsIcon class="copy-icon" name="copy" /></span>
                            </template>
                        </DsTableGridColumn>

                        <DsTableGridColumn
                            :label="$t('settings.email.domainAuthStatus.labels.status')"
                            width="35"
                        >
                            <template #default="{ verified }">
                                <DsStatus
                                    v-if="verified"
                                    :emphasis="verified.emphasis"
                                    :type="verified.type"
                                    class="dns-record-status"
                                >
                                    {{ verified.label }}
                                </DsStatus>
                            </template>
                        </DsTableGridColumn>
                    </DsTableGrid>
                </div>

                <div class="status-actions">
                    <div class="status-actions-leading">
                        <DsFilledButton
                            class="finish-button"
                            @click="close"
                        >
                            {{ $t('global.finish') }}
                        </DsFilledButton>

                        <DsTextButton
                            as="a"
                            href="https://learn.thryv.com/hc/en-us/articles/37241726237069-Why-Domain-Authentication-is-Required-with-Keap"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span>{{ $t('global.getHelp') }}</span>
                        </DsTextButton>
                    </div>

                    <DsOutlineButton
                        class="edit-domain-button"
                        @click="editDomain"
                    >
                        {{ $t('settings.email.domainAuthStatus.labels.editDomain') }}
                    </DsOutlineButton>
                </div>
            </template>
        </DsModal>
    </div>
</template>

<script>
import {
    EMAIL_AUTH_TYPE_SPF,
    EMAIL_AUTH_TYPE_S1,
    EMAIL_AUTH_TYPE_S2,
    EMAIL_AUTH_TYPE_DMARC,
} from '@/components/settings/settings.constants';
import axios from 'axios';

const TYPE_VERIFIED = 'good';
const TYPE_ERROR = 'error';
const TYPE_PENDING = 'warning';
const EMPHASIS_HIGH = 'high';
const DMARC_PCT = 5;

export default {
    props: {
        isOpen: Boolean,
        title: String,
        appId: String,
        domain: Object,
        dnsRecords: Array,
        environment: {
            type: String,
            default: '',
        },
    },

    emits: ['close', 'retry'],

    data() {
        return {
            isRefreshing: false,
        };
    },

    computed: {

        tableRecords() {
            const tableRecords = [];

            if (!this.domain) {
                return tableRecords;
            }

            const selectorPrefix = this.appId;

            const savedSPFRecord = this.dnsRecords?.find((value) => value?.emailAuthType === EMAIL_AUTH_TYPE_SPF);
            const savedS1Record = this.dnsRecords?.find((value) => value?.emailAuthType === EMAIL_AUTH_TYPE_S1);
            const savedS2Record = this.dnsRecords?.find((value) => value?.emailAuthType === EMAIL_AUTH_TYPE_S2);
            const savedDmarcRecord = this.dnsRecords?.find((value) => value?.emailAuthType === EMAIL_AUTH_TYPE_DMARC);

            const SPF_RECORD = {
                recordNumber: 1,
                recordType: 'CNAME',
                name: savedSPFRecord?.domainId || this.appId,
                host: savedSPFRecord?.dkimName || this.appId,
                emailAuthType: EMAIL_AUTH_TYPE_SPF,
                value: this.cnameHosts.EMAIL_AUTH_TYPE_SPF,
                verified: this.verificationStatus(savedSPFRecord),
            };

            tableRecords.push(SPF_RECORD);

            const S1_RECORD = {
                recordNumber: 2,
                recordType: 'CNAME',
                name: savedS1Record?.domainId || `${selectorPrefix}1`,
                host: savedS1Record?.dkimName || `${selectorPrefix}1._domainkey`,
                emailAuthType: EMAIL_AUTH_TYPE_S1,
                value: this.cnameHosts.EMAIL_AUTH_TYPE_S1,
                verified: this.verificationStatus(savedS1Record),
            };

            tableRecords.push(S1_RECORD);

            const S2_RECORD = {
                recordNumber: 3,
                recordType: 'CNAME',
                name: savedS2Record?.domainId || `${selectorPrefix}2`,
                host: savedS2Record?.dkimName || `${selectorPrefix}2._domainkey`,
                emailAuthType: EMAIL_AUTH_TYPE_S2,
                value: this.cnameHosts.EMAIL_AUTH_TYPE_S2,
                verified: this.verificationStatus(savedS2Record),
            };

            tableRecords.push(S2_RECORD);

            const DMARC_RECORD = {
                recordNumber: 4,
                recordType: 'TXT',
                name: '_dmarc',
                host: '_dmarc',
                emailAuthType: EMAIL_AUTH_TYPE_DMARC,
                value: this.generateDmarcRecord({ policy: 'quarantine', pct: DMARC_PCT }),
                verified: this.verificationStatus(savedDmarcRecord),
            };

            tableRecords.push(DMARC_RECORD);

            return tableRecords;
        },

        cnameHosts() {
            let environmentSeparator = '';

            switch (this.environment.toLowerCase()) {
            case 'integration':
                environmentSeparator = 'intg.';
                break;
            case 'staging':
                environmentSeparator = 'stge.';
                break;
            case 'development':
                environmentSeparator = 'intg.';
                break;
            default:
                environmentSeparator = '';
            }

            return {
                EMAIL_AUTH_TYPE_SPF: `return.${environmentSeparator}infusionmail.com`,
                EMAIL_AUTH_TYPE_S1: `s1.domainkey.${environmentSeparator}infusionmail.com`,
                EMAIL_AUTH_TYPE_S2: `s2.domainkey.${environmentSeparator}infusionmail.com`,
            };
        },
    },

    methods: {
        close() {
            this.$emit('close');
        },

        async refresh() {
            if (!this.isRefreshing) {
                setTimeout(() => {
                    this.isRefreshing = false;
                }, 5000);
                this.isRefreshing = true;

                try {
                    await axios.post(`/app/emailAuthorization/${this.domain.domain}/retryDomainVerification`);

                    this.isRefreshing = false;
                    this.$emit('retry');
                } catch (_) {
                    this.isRefreshing = false;
                    this.$error({
                        message: this.$t('email.auth.domainRefreshError'),
                    });
                }
            }
        },

        generateDmarcRecord(dmarc) {
            let record = `v=DMARC1; p=${dmarc.policy};`;

            if (dmarc.pct !== null && dmarc.policy !== 'none') {
                record += ` pct=${dmarc.pct};`;
            }

            if (dmarc.rua?.length && dmarc.policy !== 'none') {
                record += ` rua=mailto:${dmarc.rua};`;
            }

            return record;
        },

        copy(value) {
            const successHeadline = this.$t('settings.email.domainAuthStatus.copy.successHeadline');
            const successMessage = this.$t('settings.email.domainAuthStatus.copy.successMessage', { value });

            return this.$copyText(value).then(() => {
                this.$toast({
                    headline: successHeadline,
                    message: successMessage,
                });
            });
        },

        editDomain() {
            this.$emit('edit-domain', this.domain);
        },

        verificationStatus(dkimRecord) {
            if (!dkimRecord) {
                return {
                    emphasis: EMPHASIS_HIGH,
                    type: TYPE_ERROR,
                    label: this.$t('settings.email.domainAuthStatus.status.error'),
                };
            }

            if (dkimRecord.verified) {
                return {
                    emphasis: EMPHASIS_HIGH,
                    type: TYPE_VERIFIED,
                    label: this.$t('settings.email.domainAuthStatus.status.verified'),
                };
            }

            return {
                emphasis: EMPHASIS_HIGH,
                type: TYPE_PENDING,
                label: this.$t('settings.email.domainAuthStatus.status.pending'),
            };
        },
    },
};

</script>

<style lang="scss" rel="stylesheet/scss" type="test/scss" scoped>
.container {
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    :deep() .modal-body-inner {
        padding: $spacing-250;
    }
    :deep() .modal-body {
        padding: 0;
    }
    :deep() h4 {
        font-size: 1.25rem;
    }
    .refresh-spinner-container {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        padding: $spacing-100;
    }
}

.record-table {
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: $spacing-300;
    :deep() thead {
        border-top: 0;
    }
    :deep() td {
        vertical-align: middle;
    }
    :deep() .table-grid tr:last-child {
        border-bottom: 1px solid $color-gray-200;
    }
}

.copy-input {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    text-wrap: pretty;
}

.status-actions {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    .status-actions-leading {
        gap: $spacing-100;
        display: inline-flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-items: flex-start;
        .finish-button {
            background-color: $color-blue-700;
            color: $color-white;
        }
    }
    .edit-domain-button {
        color: $color-blue-700;
    }
}

@media($small) {
    .container {
        width: 100%;
    }

    .copy-input {
        text-wrap: unset;
    }
}

.copy-input:hover {
    cursor: pointer;
}

.copy-icon {
    margin-left: $spacing-100;
    color: $color-ink-900;
}

</style>
